import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useCatalog from '../lib/useCatalog';
import { useCatalogStatus } from '../lib/Catalog';
import { useLibrary } from '../lib/Library';
import Art from './Art';
import PosterCard from './PosterCard';
import CatalogNotice from './CatalogNotice';
import ReleaseSearch from './ReleaseSearch';

const TABS = [
  { key: 'all', label: 'All', count: (c) => c.all },
  { key: 'movie', label: 'Films', count: (c) => c.films },
  { key: 'series', label: 'Series', count: (c) => c.series },
  { key: 'people', label: 'People', count: (c) => c.people }
];

const Search = () => {
  const [params, setParams] = useSearchParams();
  const query = params.get('q') || '';
  const [draft, setDraft] = useState(query);
  const [tab, setTab] = useState('all');
  const { has, toggle } = useLibrary();
  const { can } = useCatalogStatus();
  const navigate = useNavigate();

  useEffect(() => setDraft(query), [query]);

  // Search as you type: the URL (and so the fetch) follows the draft after a
  // pause. The param drives everything, so back/forward and shared links still
  // work, and useCatalog drops stale answers when the path moves on.
  useEffect(() => {
    const trimmed = draft.trim();
    if (trimmed === query) return undefined;

    const timer = setTimeout(() => {
      setParams(trimmed ? { q: trimmed } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [draft, query, setParams]);

  const { data, loading, error, notConfigured } = useCatalog(
    query ? `/api/catalog/search?q=${encodeURIComponent(query)}` : null
  );

  // Enter still works for anyone who types faster than the debounce
  const onSubmit = (event) => {
    event.preventDefault();
    setParams(draft.trim() ? { q: draft.trim() } : {}, { replace: true });
  };

  const counts = data?.counts || {};
  const titles = (data?.titles || []).filter((title) => tab === 'all' || title.type === tab);
  const best = data?.best;

  return (
    <div className="page">
      <form className="searchbar" onSubmit={onSubmit}>
        <span className="searchbar-ring" />
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Search every film, series and person…"
          aria-label="Search the catalog"
        />
        {query && !notConfigured && (
          <span className="searchbar-count">
            {loading ? 'searching…' : `${data?.total ?? 0} results · ${data?.tookMs ?? 0}ms`}
          </span>
        )}
      </form>

      {!query ? (
        <div className="empty">Type a title, a person, or a half-remembered fragment.</div>
      ) : notConfigured ? (
        <>
          <CatalogNotice />
          {/* No catalog, but there is still a working release index behind it */}
          <ReleaseSearch query={query} />
        </>
      ) : (
        <>
          <div className="tabs">
            {TABS.filter((entry) => entry.key !== 'people' || can('people')).map((entry) => (
              <button
                key={entry.key}
                type="button"
                className={`tab${tab === entry.key ? ' is-active' : ''}`}
                onClick={() => setTab(entry.key)}
              >
                {entry.label}
                <span className="tab-count">{entry.count(counts) ?? 0}</span>
              </button>
            ))}
          </div>

          {error && <div className="alert">{error}</div>}

          {/* The server spell-checked the query; say so, because results for
              a query nobody typed are otherwise spooky. */}
          {!loading && data?.corrected && (
            <div className="search-corrected">
              Showing results for <strong>{data.corrected.to}</strong> — searched as “
              {data.corrected.from}”.
            </div>
          )}

          {loading ? (
            <div className="loading">SEARCHING</div>
          ) : (
            <>
              {best && tab !== 'people' && (
                <div className="best">
                  <Art
                    src={best.poster}
                    alt={best.name}
                    label="POSTER"
                    className="art--poster best-poster"
                  />
                  <div className="best-body">
                    <div className="kicker" style={{ marginBottom: 10 }}>
                      BEST MATCH
                    </div>
                    <div className="best-head">
                      <h2 className="best-name">{best.name}</h2>
                      <span className="best-year">{best.year || ''}</span>
                    </div>
                    <div className="meta-line" style={{ marginBottom: 14 }}>
                      {best.rating ? <span className="meta-star">★ {best.rating}</span> : null}
                      {best.runtime && <span>{best.runtime}</span>}
                      {best.genres?.length ? <span>{best.genres.join(' · ')}</span> : null}
                      <span>{best.type === 'series' ? 'Series' : 'Film'}</span>
                    </div>
                    {best.overview && <p className="best-blurb">{best.overview}</p>}
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        className="btn btn--md btn--play"
                        onClick={() =>
                          navigate(`/title/${best.type}/${best.id}`, { state: { autoplay: true } })
                        }
                      >
                        <span className="play-tri play-tri--sm" />
                        Play
                      </button>
                      <Link to={`/title/${best.type}/${best.id}`} className="btn btn--md">
                        Details
                      </Link>
                      <button type="button" className="btn btn--md" onClick={() => toggle(best)}>
                        {has(best.type, best.id) ? 'In my list' : '+ My list'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {tab !== 'people' && (
                <>
                  <h3 className="list-title">Titles</h3>
                  {titles.length === 0 ? (
                    <div className="empty">Nothing here for “{query}”.</div>
                  ) : (
                    <div className="grid">
                      {titles.map((title) => (
                        <PosterCard
                          key={`${title.type}-${title.id}`}
                          title={title}
                          variant="grid"
                        />
                      ))}
                    </div>
                  )}
                </>
              )}

              {(tab === 'all' || tab === 'people') && data?.people?.length > 0 && (
                <>
                  <h3 className="list-title" style={{ margin: '34px 0 16px' }}>
                    People
                  </h3>
                  <div className="people">
                    {data.people.map((person) => (
                      <Link key={person.id} to={`/person/${person.id}`} className="person person--link">
                        <Art src={person.photo} alt={person.name} className="art--round">
                          {!person.photo && <span className="person-label">HEADSHOT</span>}
                        </Art>
                        <div className="person-name">{person.name}</div>
                        <div className="person-role">{person.role}</div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Search;
